import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
    getNotifications,
    markNotificationRead,
    markAllNotificationsRead,
    deleteNotification,
    clearNotifications,
    notifyNotificationsUpdated,
} from '../api/notifications';
import { useTranslation } from '../i18n';
import '../styles/NotificationsPage.css';

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

const formatRelativeTime = (timestamp, t, language) => {
    if (!timestamp) return '';
    const then = new Date(timestamp);
    const elapsed = Date.now() - then.getTime();
    if (Number.isNaN(elapsed)) return '';
    if (elapsed < MINUTE) return t('notifications.relativeTime.justNow');
    if (elapsed < HOUR) {
        const mins = Math.floor(elapsed / MINUTE);
        return t('notifications.relativeTime.minutesAgo', { count: mins });
    }
    if (elapsed < DAY) {
        const hours = Math.floor(elapsed / HOUR);
        return t('notifications.relativeTime.hoursAgo', { count: hours });
    }
    if (elapsed < 7 * DAY) {
        const days = Math.floor(elapsed / DAY);
        return t('notifications.relativeTime.daysAgo', { count: days });
    }
    return then.toLocaleDateString(language, { month: 'short', day: 'numeric' });
};

const NotificationsPage = () => {
    const history = useHistory();
    const { t, language } = useTranslation();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let mounted = true;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const result = await getNotifications();
                if (mounted) setNotifications(result.notifications || []);
            } catch (err) {
                if (mounted) {
                    setError(err.message || t('notifications.errors.loadFailed'));
                }
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, [t]);

    const handleOpen = async (notification) => {
        if (!notification.read) {
            setNotifications((prev) => prev.map((n) => (
                n.id === notification.id ? { ...n, read: true } : n
            )));
            try {
                await markNotificationRead(notification.id);
                notifyNotificationsUpdated();
            } catch (err) {
                // The badge will resync on the next fetch.
            }
        }
        if (notification.link) {
            history.push(notification.link);
        }
    };

    const handleMarkAllRead = async () => {
        setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
        try {
            await markAllNotificationsRead();
            notifyNotificationsUpdated();
        } catch (err) {
            setError(err.message || t('notifications.errors.markAllReadFailed'));
        }
    };

    const handleDelete = async (event, notificationId) => {
        event.stopPropagation();
        const previous = notifications;
        setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
        try {
            await deleteNotification(notificationId);
            notifyNotificationsUpdated();
        } catch (err) {
            setNotifications(previous);
            setError(err.message || t('notifications.errors.deleteFailed'));
        }
    };

    const handleClearAll = async () => {
        const previous = notifications;
        setNotifications([]);
        try {
            await clearNotifications();
            notifyNotificationsUpdated();
        } catch (err) {
            setNotifications(previous);
            setError(err.message || t('notifications.errors.clearFailed'));
        }
    };

    if (loading) {
        return (
            <div className="notif-page">
                <div className="notif-container">
                    <div className="notif-loading">
                        <div className="notif-spinner"></div>
                        <p>{t('notifications.page.loading')}</p>
                    </div>
                </div>
            </div>
        );
    }

    const unreadCount = notifications.filter((n) => !n.read).length;

    return (
        <div className="notif-page">
            <div className="notif-container">

                <div className="notif-header">
                    <div>
                        <button className="notif-back-btn" onClick={() => history.goBack()}>
                            {t('notifications.page.back')}
                        </button>
                        <h2>{t('notifications.page.title')}</h2>
                        <p className="notif-header-sub">
                            {unreadCount > 0
                                ? t('notifications.page.headerUnread', { count: unreadCount })
                                : t('notifications.page.headerDefault')}
                        </p>
                    </div>
                    <div className="notif-header-actions">
                        {unreadCount > 0 && (
                            <button className="notif-action-btn" onClick={handleMarkAllRead}>
                                {t('notifications.page.markAllRead')}
                            </button>
                        )}
                        {notifications.length > 0 && (
                            <button className="notif-action-btn notif-action-btn--danger" onClick={handleClearAll}>
                                {t('notifications.page.clearAll')}
                            </button>
                        )}
                    </div>
                </div>

                {error && (
                    <div className="notif-error">
                        <span>⚠️</span>
                        <p>{error}</p>
                    </div>
                )}

                {notifications.length === 0 ? (
                    <div className="notif-empty">
                        <span className="notif-empty-icon">🔔</span>
                        <p>{t('notifications.page.emptyTitle')}</p>
                        <p className="notif-empty-sub">
                            {t('notifications.page.emptySubtitle')}
                        </p>
                    </div>
                ) : (
                    <div className="notif-list">
                        {notifications.map((notification) => (
                            <div
                                key={notification.id}
                                className={`notif-card ${notification.read ? '' : 'notif-card--unread'}`}
                                onClick={() => handleOpen(notification)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleOpen(notification);
                                    }
                                }}
                            >
                                <span className="notif-card-icon">{notification.icon || '🔔'}</span>
                                <div className="notif-card-body">
                                    <p className="notif-card-title">{notification.title}</p>
                                    {notification.body && (
                                        <p className="notif-card-text">{notification.body}</p>
                                    )}
                                    <p className="notif-card-time">
                                        {formatRelativeTime(notification.created_at, t, language)}
                                    </p>
                                </div>
                                {!notification.read && <span className="notif-card-dot"></span>}
                                <button
                                    className="notif-card-delete"
                                    onClick={(e) => handleDelete(e, notification.id)}
                                    aria-label={t('notifications.page.deleteAriaLabel')}
                                >
                                    ×
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default NotificationsPage;
